<template>
  <div
    class="w-full h-full flex flex-col content-center bg-grey-10 text-black overflow-hidden lg:rounded-xl"
    :class="{ 'pt-12': isIos || isAndroid }"
  >
    <div
      class="h-[32px] flex flex-row justify-between items-center mx-[24px] mt-[11.5px] mb-5"
    >
      <button @click="closeFilterModal">
        <img src="../../../public/assets/icons/close.svg" alt="" />
      </button>
      <h2 class="font-bold font-aktiv-grotesk text-[20px] leading-[25px]">
        {{ filterHeader }}
      </h2>
      <p
        class="cursor-pointer font-aktiv-grotesk text-[14px] leading-[20px] text-[#00A9E0] font-semibold"
        @click="resetFilters"
      >
        {{ $t("copy.masterLayout.filterModal.resetFilters") }}
      </p>
    </div>

    <div
      class="accordion-wrapper h-full overflow-x-hidden overflow-y-scroll mx-[16px]"
    >
      <div
        v-for="item in filterItems"
        :key="item.id"
        class="checkbox mb-[8px] rounded-lg"
        :class="[
          { ' bg-[#e7f7ff]': hasChecked(item.id) },
          { ' bg-[#f8f9f9]': !hasChecked(item.id) },
        ]"
        @click="toggleSelectedItem(item.id)"
      >
        <label
          :for="item.id"
          class="flex items-center h-full relative cursor-pointer pl-[16px] py-[8px] text-[16px] leading-[24px] lg:max-w-[35ch]"
          :style="{ width: 'calc(100% - 52px)' }"
        >
          <span
            >{{ getTitle(item) }}
            <small
              v-if="item.sessionId"
              class="text-xs italic whitespace-nowrap"
              >(Session {{ item.sessionId }})</small
            ></span
          >
          <input
            :id="item.id"
            class="hidden absolute"
            type="checkbox"
            :name="item.title"
            :value="item.id"
            :checked="hasChecked(item.id)"
            :data-event-tracking="
              'filter-' + item.title.toLowerCase().replace(/[\s:]+/g, '-')
            "
            @click.stop
          />
          <div v-if="hasChecked(item.id)">
            <img
              class="absolute top-1/2 right-[-36px] w-[20px] h-[20px] rounded-sm transform -translate-y-1/2"
              src="../../../public/assets/icons/checked-icon.png"
            />
          </div>
          <div v-else>
            <img
              class="absolute top-1/2 right-[-36px] w-[20px] h-[20px] rounded-sm transform -translate-y-1/2"
              src="../../../public/assets/icons/unchecked-icon.png"
            />
          </div>
        </label>
      </div>
    </div>

    <div class="mx-[24px]">
      <BaseButton
        class="w-full h-[48px] justify-center items-center font-aktiv-grotesk leading-[24px] text-[18px] text-center py-[12px] text-white font-bold mt-[16px] mb-[100px] lg:mb-[16px]"
        @click="applyFilters"
        solid-classes="bg-[#00A9E0] text-white"
        :disabled="isFilterEmpty"
      >
        {{ $t("copy.masterLayout.filterModal.applyFilters") }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { isIosNativeOnly, isAndroidNativeOnly } from "@/utils/platform-check";
import moment from "moment-timezone";
import BaseButton from "../common/BaseButton.vue";
import FilterAccordion from "./FilterAccordion.vue";

export default {
  components: {
    BaseButton,
    FilterAccordion,
  },
  props: {
    filterItems: {
      type: Array,
      default: () => [],
    },
    filterHeader: {
      type: String,
      required: true,
    },
    filterType: {
      type: String,
      required: true,
    },
    filterTypeId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      selectedItems: [],
    };
  },
  computed: {
    ...mapState("filter", [
      "sessions",
      "filteredItems",
      "allResources",
      "selectedFilter",
    ]),
    ...mapState("cms", ["loading"]),
    isIos() {
      return isIosNativeOnly();
    },
    isFilterEmpty() {
      return this.selectedItems.length < 1;
    },
    isAndroid() {
      return isAndroidNativeOnly();
    },
  },
  mounted() {
    this.selectedItems = Object.assign([], this.filteredItems);
  },
  methods: {
    async closeFilterModal() {
      const showFilterModal = false;
      await this.$store.dispatch("filter/updateShowFilterModal", {
        showFilterModal,
      });
    },
    toggleSelectedItem(itemId) {
      if (this.selectedItems.includes(itemId)) {
        const index = this.selectedItems.indexOf(itemId);
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(itemId);
      }
    },
    async setFilters() {
      await this.$store.dispatch("filter/setSelectedFilter", {
        type: this.filterType,
        typeId: this.filterTypeId,
      });
      await this.$store.dispatch("filter/updateFilteredItems", {
        filteredItems: this.selectedItems,
      });
    },
    needClearFilter() {
      if (!this.selectedFilter || Object.keys(this.selectedFilter).length === 0)
        return false;

      const clickedFilterKey = `${this.filterType}-${this.filterTypeId}`;
      const selectedFilterKey = `${this.selectedFilter.type}-${this.selectedFilter.typeId}`;
      return selectedFilterKey !== clickedFilterKey;
    },
    async applyFilters() {
      if (this.needClearFilter()) {
        this.removePreviousFilterItems();
      }

      await this.$store.commit("filter/clearFilteredResources", {});
      await this.setFilters(); // set checkbox items
      await this.$store.dispatch("filter/updateFilteredResources"); // apply filter for the resources
      this.closeFilterModal();
    },
    async resetFilters() {
      this.selectedItems = [];
      await this.setFilters();
      await this.$store.commit("filter/clearFilteredResources", {});
    },
    removePreviousFilterItems() {
      const filterItemIds = this.filterItems.reduce((acc, item) => {
        acc.push(item.id);
        return acc;
      }, []);
      this.selectedItems = this.selectedItems.filter((id) =>
        filterItemIds.includes(id)
      );
    },
    getTitle(item) {
      return item.title;
    },
    hasChecked(itemId) {
      return this.selectedItems.includes(itemId);
    },
  },
};
</script>

<style scoped>
.accordion-wrapper::-webkit-scrollbar,
.accordion::-webkit-scrollbar {
  display: none;
}

/* On mouse-over, add a grey background color */
.checkbox:hover input ~ .checkmark {
  background-color: rgb(255, 255, 255, 0.5);
}

/* When the checkbox is checked, add a yellow background */
.checkbox input:checked ~ .checkmark {
  background-color: #00a9e0;
  border-color: #00a9e0;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
/* .checkbox input:checked ~ .checkmark:after */
.checkbox input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.checkbox .checkmark:after {
  left: 6px;
  top: 2px;
  width: 7px;
  height: 12px;
  border: solid#f7f7f7;
  border-width: 0 3px 3px 0;
  border-radius: 1px;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}
</style>
